import { fetchJSON } from './api';

export type SecPoseReannotationBlocker = {
  reason: string;
  detail: string;
};

export type SecPoseReannotationPlanItem = {
  episode_id: string;
  status: string | null;
  secpose_cycle: number | null;
  annotation_frame_count: number | null;
  expected_counter_delta: number | null;
  eligible: boolean;
  blockers: SecPoseReannotationBlocker[];
  ddb: {
    row_exists: boolean;
    secpose_done_present: boolean;
    secpose_done_value: boolean | null;
  };
  r2: {
    key: string;
    exists: boolean;
    etag: string | null;
    size: number | null;
  };
};

export type SecPoseReannotationPlan = {
  plan_id: string;
  expires_at_ms: number;
  confirm_phrase: string;
  reason: string;
  stats_policy: 'preserve';
  allow_missing_r2: boolean;
  eligible: boolean;
  items: SecPoseReannotationPlanItem[];
};

export type SecPoseReannotationJob = {
  job_id: string;
  status: string;
  created_by: string;
  reason: string;
  created_at_ms: number;
  updated_at_ms: number;
  completed_at_ms: number | null;
  error: string | null;
};

export type SecPoseReannotationJobItem = {
  job_id: string;
  episode_id: string;
  source_status: string;
  source_cycle: number;
  target_cycle: number;
  annotation_frame_count: number;
  source_r2_missing: number;
  archive_r2_key: string;
  phase: string;
  error: string | null;
};

export type SecPoseReannotationJobView = {
  job: SecPoseReannotationJob;
  items: SecPoseReannotationJobItem[];
};

export type SecPoseReannotationHistory = {
  current: {
    episode_id: string;
    status: string | null;
    secpose_cycle: number | null;
  };
  history: Array<{
    history_id: number;
    job_id: string;
    episode_id: string;
    cycle: number;
    operator: string;
    kind: string;
    archive_key: string;
    archived_at_ms: number;
    archived_by: string;
    reason: string;
  }>;
  jobs: SecPoseReannotationJob[];
};

type PlanInput = {
  episode_ids: string[];
  reason: string;
  stats_policy: 'preserve';
  allow_missing_r2?: boolean;
};

export async function planSecPoseReannotation(input: PlanInput): Promise<SecPoseReannotationPlan> {
  const result = await fetchJSON<SecPoseReannotationPlan>('/api/secpose/admin/reannotation/plan', {
    method: 'post',
    json: input,
  });
  if (!result) throw new Error('预检没有返回结果');
  return result;
}

export async function executeSecPoseReannotation(
  input: PlanInput & {
    plan_id: string;
    idempotency_key: string;
    confirm: string;
  }
): Promise<SecPoseReannotationJobView> {
  const result = await fetchJSON<SecPoseReannotationJobView>(
    '/api/secpose/admin/reannotation/execute',
    { method: 'post', json: input }
  );
  if (!result) throw new Error('执行没有返回结果');
  return result;
}

export async function getSecPoseReannotationHistory(
  episodeId: string
): Promise<SecPoseReannotationHistory> {
  const result = await fetchJSON<SecPoseReannotationHistory>(
    `/api/secpose/admin/episodes/${encodeURIComponent(episodeId)}/reannotation-history`
  );
  if (!result) throw new Error('历史记录没有返回结果');
  return result;
}

export async function runSecPoseReannotationJobAction(
  jobId: string,
  action: 'resume' | 'abort' | 'rollback'
): Promise<SecPoseReannotationJobView> {
  const result = await fetchJSON<SecPoseReannotationJobView>(
    `/api/secpose/admin/reannotation/jobs/${encodeURIComponent(jobId)}/${action}`,
    { method: 'post' }
  );
  if (!result) throw new Error('任务操作没有返回结果');
  return result;
}
